import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import expressCheckout from './expressCheckout';

function getQueryStringValue (key) { // Old solution that mantains old browser support https://stackoverflow.com/questions/9870512/how-to-obtain-the-query-string-from-the-current-url-with-javascript
  // eslint-disable-next-line no-useless-escape
  return decodeURIComponent(window.location.search.replace(new RegExp(`^(?:.*[&\\?]${encodeURIComponent(key).replace(/[\.\+\*]/g, '\\$&')}(?:\\=([^&]*))?)?.*$`, 'i'), '$1'));
}

// We keep the storage session for 1day -> Clean older sessions
const isImpersonateMode = !!getQueryStringValue('impersonateData');

const removeSessionsAfter = isImpersonateMode ? 5400 : 86400; // (1hour : 1day) in seconds
const currentDate = new Date();

for (let i = localStorage.length - 1; i >= 0; i -= 1) {
  const itemKey = window.localStorage.key(i);

  try {
    let itemNumber = Number(itemKey);

    if (Math.abs(Date.now() - itemNumber) > Math.abs(Date.now() - itemNumber * 1000)) {
      itemNumber *= 1000;
    }

    const storeDate = new Date(itemNumber);
    const dif = currentDate.getTime() - storeDate.getTime();

    if (dif > removeSessionsAfter * 1000) {
      window.localStorage.removeItem(itemKey);
    }
  } catch {
    // No number, ignoring
  }
}

// We get the current window key
let storeKey = window.sessionStorage.getItem('windowKey');

// We check if we are entry from a new "session"
const isNewEntrance = !!getQueryStringValue('workspace') && !!getQueryStringValue('product') && !!getQueryStringValue('offer');

// If the user cameback from gateway and it has a reference, we find the reference in the local storage the associate the order reference
if (!isNewEntrance && !storeKey && getQueryStringValue('reference')) { // window.location.pathname && window.location.pathname.toLowerCase().endsWith('/gateway')
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const itemKey = window.localStorage.key(i);
    let itemValue = window.localStorage.getItem(itemKey);
    let isJson = false;

    try {
      itemValue = JSON.parse(itemValue);
      isJson = true;
    } catch {
    // Not json
    }

    if (isJson && itemValue && getQueryStringValue('reference').includes(itemValue.bookingId)) {
      storeKey = itemKey;
      sessionStorage.setItem('windowKey', storeKey);
    }
  }
}

// If we open the url in the new tab, we grab the most recent
if (!isNewEntrance && !storeKey && window.localStorage.length) {
  let mostRecent = 0;
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const itemKey = window.localStorage.key(i);
    try {
      const itemNumber = Number(itemKey);
      if (itemNumber > mostRecent) {
        mostRecent = itemNumber;

        storeKey = itemKey;
        sessionStorage.setItem('windowKey', storeKey);
      }
    } catch {
      // No number, ignoring
    }
  }
}

// Set new key
if (!storeKey) {
  storeKey = Date.now();
  sessionStorage.setItem('windowKey', storeKey);
}

function initialState (siteInfo) {
  return {
    loading: false,
    siteInfo: siteInfo || null,
    product: null,
    products: [],
    offerInfo: null,
    cart: {},
    cartItem: null,
    user: null,
    currentParticipant: null,
    availableOptions: null,
    sessions: null,
    stripeSessionID: null,
    // Current step in the funnel
    flow: 'Configure', // configure  -> When we click next, we want to avoid user opens previous or next pages by link. changeToPage(name) changes the flow and move the page,
    order: null,
    error: null,
    errorMessage: null,
    promoCampaign: null,
    sessionsCodes: [],
    pageUrl: null,
    impersonateData: null,
    impersonateCollaborator: null,
    newTDA: false,
    showParticipantStep: false,
    showSessionsStep: false,
    showTariffsStep: true,
    visitedSteps: [],
    selectedPeriod: null,
    subscriptionTDA: false,
    formValid: null,
    subscriptionAction: null,
    showCustomerForm: null,
    showEmergencyContactForm: null,
    formError: null,
    subscriptionHeader: {},
    promoAmount: null,
    promoAmountFee: null,
    promoMetric: null,
    backNavigation: false,
    recurringPromo: false,
    productServices: [],
    amplifySubscription: null,
    availableDates: {},
    courtSettings: {},
    contractToRecharge: null,
    singlePurchaseOptions: [],
    preReservationOrder: null
  };
}

// Missing store key system
const store = createStore({
  plugins: [createPersistedState({
    key: storeKey,
    paths: ['courtSettings', 'availableDates', 'productServices', 'siteInfo', 'flow', 'product', 'user', 'offerInfo', 'cartItem', 'cart', 'currentParticipant', 'availableOptions', 'sessions', 'stripeSessionID', 'orders', 'order', 'products', 'promoCampaign', 'sessionsCodes', 'pageUrl', 'impersonateData', 'newTDA', 'visitedSteps', 'impersonateCollaborator', 'selectedPeriod', 'subscriptionTDA', 'contractToRecharge', 'singlePurchaseOptions', 'expressCheckout']
  })],
  state: initialState,
  mutations: {
    reset (state) {
      // acquire initial state
      const resetedState = initialState(state.siteInfo);
      Object.assign(state, resetedState);
    },
    setCourtSettings (state, data) {
      state.courtSettings = data;
    },
    setPage (state, data) {
      state.page = data;
    },
    setSiteInfo (state, data) {
      state.siteInfo = data;
    },
    setProduct (state, data) {
      state.product = data;
    },
    setProducts (state, data) {
      state.products = Array.isArray(data) ? data : [];
    },
    setUser (state, data) {
      state.user = data;
    },
    setCurrentParticipant (state, data) {
      state.currentParticipant = data;
    },
    setCurrentParticipantGymlibCode (state, data) {
      state.currentParticipant.gymlibCode = data;
    },
    setOfferInfo (state, data) {
      state.offerInfo = data;
    },
    addCartItemToCart (state, data) {
      state.cart.items = data;
    },
    setCartItem (state, data) {
      state.cartItem = data;
    },
    setSessions (state, data) {
      const sessions = {
        items: data,
        numParticipants: state.cartItem?.participants?.length || 1
      };
      state.sessions = sessions;
    },
    setCartItemParticipants (state, data) {
      const participants = [...state.cartItem.participants];
      const selectedIndex = participants.findIndex(c => c.uuid === data.uuid);
      if (selectedIndex === -1) {
        participants.push(data);
      } else {
        participants[selectedIndex] = data;
      }
      state.cartItem.participants = participants;
    },
    manageCartItemParticipant (state, { index, participant }) {
      if (!participant) {
        const participants = state.cartItem.participants || [];
        participants.splice(index, 1);
        state.cartItem.participants = participants;
        return;
      }
      state.cartItem.participants[index] = participant;
    },
    setNewTDA (state, data) {
      state.newTDA = data;
    },
    setSubscriptionTDA (state, data) {
      state.subscriptionTDA = data;
    },
    setAvailableOptions (state, data) {
      state.availableOptions = data;
    },
    setStripeSessionID (state, data) {
      state.stripeSessionID = data;
    },
    setLoading (state, data) {
      state.loading = data;
    },
    setFlow (state, data) {
      state.flow = data;
    },
    setOrder (state, data) {
      state.order = data;
    },
    setError (state, error) {
      state.error = error;
    },
    setErrorMessage (state, error) {
      state.errorMessage = error;
    },
    resetError (state) {
      state.error = initialState().error;
    },
    setPromoCampaign (state, data) {
      state.promoCampaign = data;
    },
    setSessionsCodes (state, data) {
      state.sessionsCodes = data;
    },
    setPageUrl (state, data) {
      state.pageUrl = data;
    },
    setImpersonateData (state, data) {
      state.impersonateData = data;
    },
    setImpersonateCollaborator (state, data) {
      state.impersonateCollaborator = data;
    },
    setSteps (state, data) {
      state.steps = data;
    },
    addCartItemParticipantTariff (state, { participant, tariff }) {
      const participants = [...state.cartItem.participants];
      const selectedIndex = participants.findIndex(c => c.uuid === participant.uuid);
      participants[selectedIndex].tariff = tariff;
      state.cartItem.participants = participants;
    },
    resetParticipantQuantity (state, { participant }) {
      const participants = [...state.cartItem.participants];
      const selectedIndex = participants.findIndex(c => c.uuid === participant.uuid);
      participants[selectedIndex].quantity = 1;
      state.cartItem.participants = participants;
    },
    handleCartItemParticipants (state, data) {
      const participants = [...state.cartItem.participants];
      const selectedIndex = participants.findIndex(c => c.uuid === data.uuid);
      if (selectedIndex !== -1) {
        participants.splice(selectedIndex, 1);
      } else {
        participants.push(data);
      }
      state.cartItem.participants = participants;
    },
    changeParticipantQuantity (state, { participant, quantity }) {
      const participants = [...state.cartItem.participants];
      const selectedIndex = participants.findIndex(c => c.uuid === participant.uuid);
      participants[selectedIndex].quantity += quantity;
      state.cartItem.participants = participants;
    },
    addVisitedStep (state, data) {
      const visitedSteps = [...state.visitedSteps];
      if (!visitedSteps.includes(data)) {
        visitedSteps.push(data);
      }
      state.visitedSteps = visitedSteps;
    },
    clearVisitedSteps (state) {
      state.visitedSteps = [];
    },
    setShowParticipantStep (state, data) {
      state.showParticipantStep = data;
    },
    setShowSessionsStep (state, data) {
      state.showSessionsStep = data;
    },
    setShowTariffsStep (state, data) {
      state.showTariffsStep = data;
    },
    setSelectedPeriod (state, data) {
      state.selectedPeriod = data;
    },
    setFormValid (state, data) {
      state.formValid = data;
    },
    setSubscriptionAction (state, data) {
      state.subscriptionAction = data;
    },
    setShowCustomerForm (state, data) {
      state.showCustomerForm = data;
    },
    setShowEmergencyContactForm (state, data) {
      state.showEmergencyContactForm = data;
    },
    setSubscriptionPayment (state, data) {
      state.subscriptionPayment = data;
    },
    setFormError (state, data) {
      state.formError = data;
    },
    setSubscriptionHeader (state, data) {
      state.subscriptionHeader = data;
    },
    setPromoAmount (state, data) {
      state.promoAmount = data;
    },
    setPromoAmountFee (state, data) {
      state.promoAmountFee = data;
    },
    setPromoMetric (state, data) {
      state.promoMetric = data;
    },
    setBackNavigation (state, data) {
      state.backNavigation = data;
    },
    setRecurringPromo (state, data) {
      state.recurringPromo = data;
    },
    setProductServices (state, data) {
      state.productServices = data;
    },
    setAmplifySubscription (state, data) {
      state.amplifySubscription = data;
    },
    setAvailableDates (state, data) {
      state.availableDates = data;
    },
    setContractToRecharge (state, data) {
      state.contractToRecharge = data;
    },
    setSinglePurchaseOptions (state, data) {
      state.singlePurchaseOptions = data;
    },
    setPreReservationOrder (state, data) {
      state.preReservationOrder = data;
    }
  },
  actions: { },
  modules: { expressCheckout },
  getters: {
    workspace: state => (state.siteInfo ? state.siteInfo.workspace : null),
    siteURL: state => {
      if (state.siteInfo) {
        return `${process.env.VUE_APP_UCPA_DOMAIN}/${state.siteInfo.site_preroute}/${state.siteInfo.site_url}`;
      }
      return null;
    },
    // TODO remove later -> apply the below condition (will not work on localhost)
    alphaURL: state => {
      if (state.siteInfo) {
        return `${process.env.VUE_APP_UCPA_DOMAIN}${state.pageUrl || (`/${state.siteInfo.site_preroute}/${state.siteInfo.site_url}`)}`;
      }
      return `${process.env.VUE_APP_UCPA_DOMAIN}`;

      // switch (process.env.VUE_APP_ENVIRONMENT) {
      //   case 'dev':
      //     return `http://localhost:${process.env.VUE_APP_ALPHA_PORT}/${state.siteInfo.site_preroute}/${state.siteInfo.site_url}`;
      //   case 'staging':
      //     return `https://recette.front.ucpa.com/${state.siteInfo.site_preroute}/${state.siteInfo.site_url}`;
      //   case 'production':
      //     return `https://www.ucpa.com/${state.siteInfo.site_preroute}/${state.siteInfo.site_url}`;
      // }

      // return `/${state.siteInfo.site_preroute}/${state.siteInfo.site_url}`;
    },
    isImpersonateMode: state => !!state.impersonateData,
    isRaquettes: state => state.product && (state.product.areas || []).some(a => a.name === 'Raquettes'),
    isEscapeGame: state => state.product && (state.product.areas || []).some(a => a.showMultipleOffers),
    isCartContractRecharge: state => !!state.contractToRecharge
  }
});

export default store;
export const useStore = () => store;
